/**
 * The registry of named entity bindings.
 *
 * A binding is a durable slot connecting a flow in code to an entity that
 * exists only in the user's database — "the account E-ZPass imports go to",
 * "the category benefit fees use". Code cannot name such entities directly
 * (they are user-created), so a flow declares a slot here and resolves it at
 * use time; the user's choice is stored in Settings.bindings and reused until
 * changed.
 *
 * Keys are code-owned: adding a flow means adding a key here. Values are
 * user-owned: which entity a key resolves to lives only in the database, set
 * via the setBinding mutation (typically remembered automatically the first
 * time the flow runs).
 */

export type BindingEntityType = 'account' | 'category' | 'field';

export type BindingSpec = {
  entityType: BindingEntityType;
  /** Human-readable slot name, for settings UI and error messages. */
  label: string;
  /** What the slot does — shown under the label on the Defaults page. */
  description: string;
  /**
   * For account slots whose flow only works in one currency: pickers and
   * prefills restrict to accounts in this currency. Absent means any
   * currency (the flow checks compatibility at use time instead).
   */
  currency?: string;
};

export type BindingKey =
  | 'cashAccount'
  | 'ezpassImportAccount'
  | 'paycheckCategory'
  | 'reconciliationCategory'
  | 'retirementCategory'
  | 'taxYearField'
  | 'taxesCategory'
  | 'venmoImportAccount';

export const BINDINGS: Record<BindingKey, BindingSpec> = {
  cashAccount: {
    entityType: 'account',
    label: 'Cash account',
    description:
      'Prefilled by the "ATM withdrawal" shortcut when creating an inverse transaction',
  },
  ezpassImportAccount: {
    entityType: 'account',
    label: 'E-ZPass import account',
    description: 'Where E-ZPass imports land',
    // Imported rows are always USD, and the server rejects a raw whose
    // currency mismatches its account.
    currency: 'USD',
  },
  paycheckCategory: {
    entityType: 'category',
    label: 'Paycheck category',
    description:
      'The income category paycheck deposits land in — withheld payroll ' +
      'taxes merge into its node on the cash-flow map (deposits plus ' +
      'withholding)',
  },
  reconciliationCategory: {
    entityType: 'category',
    label: 'Reconciliation category',
    description:
      'Category for reconcile fix-up adjustments — when set, an adjustment ' +
      'is categorized the moment it is created',
  },
  retirementCategory: {
    entityType: 'category',
    label: 'Retirement category',
    description:
      'The income category 401(k)/HSA contributions land in — on the ' +
      'cash-flow map it pins beside the Saving tier, since those dollars ' +
      'are savings by definition',
  },
  taxYearField: {
    entityType: 'field',
    label: 'Tax year field',
    description:
      'The field naming which tax year a tax payment or refund settles — ' +
      'the payroll page reports taxes by tax year from it. An enum field ' +
      'gives a picker and typo-proof values; a text field works too.',
  },
  taxesCategory: {
    entityType: 'category',
    label: 'Taxes category',
    description:
      'Where withheld payroll taxes merge on the cash-flow map — tax ' +
      'payments and refunds categorized here net against the withheld band',
  },
  venmoImportAccount: {
    entityType: 'account',
    label: 'Venmo import account',
    description: 'Where Venmo imports land',
    currency: 'USD',
  },
};

export const isBindingKey = (key: string): key is BindingKey =>
  Object.prototype.hasOwnProperty.call(BINDINGS, key);

/** Every registry key, in the registry's declaration order. */
export const ALL_BINDING_KEYS: BindingKey[] =
  Object.keys(BINDINGS).filter(isBindingKey);
